import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { getMaxCapacity, useStandardizeImages } from "../data";
import { useGetPartnerOffering } from "../data/api";
import { Testimonial } from "../data/partners";
import { buildPathWithParams, useQueryString } from "../hooks";
import { FirstDayOfWeek } from "../lib/datetime";
import { useTrackProductViewedEcommerceEventOnMount } from "./analytics_ecommerce_events";
import {
  useLocationV2Query,
  useQuoteQuery,
  useSpaceV2Query,
  usePartnerCatalogQueryMaybe,
  usePhysicalSpaceQuery,
} from "./api";
import { DayOfficeCheckoutBoxDesktop } from "./checkout_box_desktop";
import { DayOfficeCheckoutBoxMobile } from "./checkout_box_mobile";
import { useCurrentQueryString, useSyncQueryString } from "./offering_helpers";
import {
  normalizeDayOfficeOrderParams,
  toQuoteInput,
  useDayOfficeOrderParamsHandlers,
  useSpaceAvailableDaysQuery,
} from "./order_params";
import { PartnerConfig } from "./partner_config";
import {
  GeneralInformation,
  OfferingDetailsBase,
} from "./template_offering_details_base";
import { FAQ } from "./template_offering_details_common";

interface TemplateOfferingDetailsDayOfficeParams {
  offeringID: string;
  spaceID: string;
}

export interface TemplateOfferingDetailsDayOfficeQueryParams {
  startDate?: string;
  endDate?: string;
  addOnID?: string;
  fromCatalog?: string;
  promoCode?: string;
}

interface TemplateOfferingDetailsDayOfficeProps {
  firstDayOfWeek?: FirstDayOfWeek;
  showTalkWithUsPrompt?: boolean;
  showAddress: boolean;
  showDirections: boolean;
  partnerConfig: PartnerConfig;
  useLegacyImages?: boolean;
  data: {
    description: React.ReactNode;
    faqs?: FAQ[];
    testimonials?: Testimonial[];
    generalInfo?: GeneralInformation;
  };
}

export function TemplateOfferingDetailsDayOffice(
  props: TemplateOfferingDetailsDayOfficeProps
) {
  const {
    partnerConfig,
    firstDayOfWeek,
    showTalkWithUsPrompt,
    showAddress,
    showDirections,
    data,
    useLegacyImages = false,
  } = props;
  const pageParams = useParams<TemplateOfferingDetailsDayOfficeParams>();
  const queryParams =
    useQueryString<TemplateOfferingDetailsDayOfficeQueryParams>();
  const partnerOfferingID = pageParams.offeringID;
  const spaceID = pageParams.spaceID;
  const partnerOffering = useGetPartnerOffering(partnerOfferingID);
  const partnerCatalog = usePartnerCatalogQueryMaybe(
    partnerOffering.partner.partnerSlug,
    queryParams.fromCatalog
  );
  const {
    orderParams,
    orderParamsError,
    handleChangeDayOfficeDateRange,
    handleChangePromoCode,
  } = useDayOfficeOrderParamsHandlers({
    ...normalizeDayOfficeOrderParams({
      ...queryParams,
      promoCode: queryParams.promoCode,
    }),
    spaceID,
  });

  useEffect(() => {
    if (partnerCatalog?.promoCode && !queryParams.promoCode) {
      handleChangePromoCode(partnerCatalog?.promoCode);
    }
  }, [handleChangePromoCode, partnerCatalog, queryParams.promoCode]);

  const { isOutsideRange, isBlockedDate, handleNextVisibleDates } =
    useSpaceAvailableDaysQuery(spaceID, {
      physicalSpaceID: orderParams.physicalSpaceID,
    });
  const physicalSpace = usePhysicalSpaceQuery(orderParams.physicalSpaceID);
  const history = useHistory();
  const space = useSpaceV2Query(spaceID);
  const [spaceLayoutID, setSpaceLayoutID] = useState<string>();
  useEffect(() => {
    if (space) setSpaceLayoutID(space.defaultLayoutID);
  }, [space, setSpaceLayoutID]);

  const quoteInput = useMemo(
    () =>
      spaceID && spaceLayoutID
        ? toQuoteInput(spaceID, partnerOffering.partner.id, orderParams, "WL", {
            layoutID: spaceLayoutID,
            attendees: 1,
          })
        : null,
    [spaceID, partnerOffering.partner.id, orderParams, spaceLayoutID]
  );
  const location = useLocationV2Query(space.locationID);
  const {
    data: quote,
    loading: quoteLoading,
    error: quoteError,
  } = useQuoteQuery(quoteInput);
  const queryString = useCurrentQueryString(orderParams);
  useSyncQueryString(orderParams);

  const handleContinue = useCallback(() => {
    const pathWithParams = buildPathWithParams(
      `/offerings/${partnerOfferingID}/spaces/${spaceID}/checkout?${queryString}`
    );
    history.push(pathWithParams);
  }, [history, partnerOfferingID, spaceID, queryString]);

  const handlePressBack = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
    } else {
      const pathWithParams = buildPathWithParams(
        `/offerings/${partnerOfferingID}`,
        { reset: true, keepOnReset: ["fromCatalog"] }
      );
      history.replace(pathWithParams);
    }
  }, [history, partnerOfferingID]);

  const images = useStandardizeImages(
    partnerOffering,
    location,
    space,
    spaceID,
    useLegacyImages,
    {
      imagesDesktop: [space.images[0], ...location.images].map((i) => i.medium),
      imagesMobile: [space.images[0], ...location.images].map((i) => i.small),
      imagesModal: [space.images[0], ...location.images].map((i) => i.large),
    }
  );
  const offeringName = `${space.productType?.name} ${physicalSpace.name}, ${location.name}`;
  useTrackProductViewedEcommerceEventOnMount(
    offeringName,
    space,
    location,
    quote
  );

  return (
    <OfferingDetailsBase
      showTalkWithUsPrompt={showTalkWithUsPrompt}
      showAddress={showAddress}
      showDirections={showDirections}
      partnerOffering={partnerOffering}
      partnerOfferingID={partnerOfferingID}
      partnerConfig={partnerConfig}
      onPressBack={handlePressBack}
      space={space}
      data={{
        ...data,
        ...images,
        location: location,
        description: space.description,
        offeringName,
        capacity: getMaxCapacity(space),
      }}
      checkoutBoxDesktop={
        <DayOfficeCheckoutBoxDesktop
          quoteError={quoteError}
          quoteLoading={quoteLoading}
          firstDayOfWeek={firstDayOfWeek}
          orderParams={orderParams}
          orderParamsErrors={orderParamsError}
          isOutsideRange={isOutsideRange}
          isBlockedDate={isBlockedDate}
          onChangeDateRange={handleChangeDayOfficeDateRange}
          onContinue={handleContinue}
          onNextVisibleDates={handleNextVisibleDates}
          location={location}
          quote={quote}
          space={space}
          datePickerPlaceholder={partnerConfig.datePickerPlaceholder}
        />
      }
      checkoutBoxMobile={
        <DayOfficeCheckoutBoxMobile
          quoteError={quoteError}
          orderParamsError={orderParamsError}
          firstDayOfWeek={firstDayOfWeek}
          orderParams={orderParams}
          isOutsideRange={isOutsideRange}
          isBlockedDate={isBlockedDate}
          onChangeDateRange={handleChangeDayOfficeDateRange}
          onContinue={handleContinue}
          onNextVisibleDates={handleNextVisibleDates}
          selectedSpace={space}
          quote={quote}
          datePickerPlaceholder={partnerConfig.datePickerPlaceholder}
        />
      }
    />
  );
}
